/*
New project component.
Props: Title, Description, Inputs, confirmAction
    Prop.ConfirmAction: function
 */

import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Box,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { ThemeProvider } from "@mui/material/styles";
//import NewProjectDialogStyles from "../../styles/jss/components/common/NewProjectDialogStyles";
import { cfg } from "../../config";
import axios from "axios";
import { fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'react-toastify';
import withRouter from "../../helpers/withRouter";
import { useNavigate } from "react-router-dom";
import theme from "../../theme";

const NewProjectDialog = (props) => {
  const navigate = useNavigate();

  const [inputValues, setInputValues] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let inputValues = {};
    for (let i = 0; i < props.inputs.length; i++) {
      inputValues[props.inputs[i].id] = "";
    }
    setInputValues(inputValues);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    const previousInputValues = inputValues;
    previousInputValues[name] = value;
    setInputValues(previousInputValues);
  };

  const createNewAnalysis = (pid) => {
    fetchAuthSession().then((session) => {
      axios
        .post(
          `${cfg.apiUrl}/app/${props.appApi}/analysis`,
          {
            projectId: pid,
          },
          {
            headers: {
              Authorization: session.tokens?.idToken,
              "Content-Type": "application/json",
            },
          }
        )
        .then((resp) => {
          if (resp.status === 200) {
            navigate(`/apps/${props.app}/analysis/${pid}/${resp.data.id}`);
          }
        });
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const { updateData } = props;
    fetchAuthSession().then((session) => {
      setLoading(true);
      axios
        .post(
          `${cfg.apiUrl}/${props.postUrl}`,
          {
            ...inputValues,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: session.tokens?.idToken,
            },
          }
        )
        .then((resp) => {
          if (resp.status === 200) {
            setLoading(false);
            props.setOpen(false);
            createNewAnalysis(resp.data.id);
            updateData();
          }
        })
        .catch((err) => {
          setLoading(false);
          if (err.response) {
            if (err.response.status === 400) {
              toast.error(
                err.response.data.error.message,
                "Error"
              );
            } else {
              toast.error(
                "An error has occurred, please try again.",
                "Error"
              );
            }
          } else if (err.request) {
            toast.error(
              "An error has occurred, please try again.",
              "Error"
            );
          } else {
            toast.error(
              "An error has occurred, please try again.",
              "Error"
            );
          }
        });
    });
  };

  const { title, description } = props;
  const { handleClose } = props;

  //TODO: Is ThemeProvider needed here?
  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      //classes={{ paper: classes.dialogContainer }}
      slotProps={{
        paper: {
          sx: {
            [theme.breakpoints.down('md')]: {
              width: '95%',
              margin: '0'
            },
            [theme.breakpoints.up('sm')]: {
                minWidth: '35%'
            }
          }
        }
      }}
    >
      <form onSubmit={(e) => submit(e)}>
        <DialogTitle id="form-dialog-title">{title}</DialogTitle>
        <DialogContent>
          {description !== null && (
            <DialogContentText style={{ paddingBottom: '10px' }}>{description}</DialogContentText>
          )}

          {props.inputs.map((input, idx) =>
            input.type === "text" ? (
              <Box mb={3} key={idx}>
                <FormControl fullWidth>
                  <TextField
                    variant="standard"
                    type={input.type}
                    id={input.id}
                    autoFocus={idx === 0}
                    name={input.id}
                    fullWidth
                    required={input.required}
                    label={input.label + (input.required ? "* (required)" : "")}
                    value={props.inputs.id}
                    onChange={(e) => handleChange(e)}
                    slotProps={{
                      inputLabel: { required: false }
                    }}
                  />
                </FormControl>
              </Box>
            ) : (
              <FormControl fullWidth key={idx}>
                <InputLabel htmlFor={input.id}>
                  {input.label + (input.required ? "* (required)" : "")}
                </InputLabel>
                <Select
                  native
                  variant="standard"
                  value={props.inputs.id}
                  id={input.id}
                  name={input.id}
                  required={input.required}
                  onChange={(e) => handleChange(e)}
                  fullWidth
                  //classes={{ select: classes.selectInput }}
                  sx={{
                    '&:focus': {
                        backgroundColor: 'rgba(0,0,0,0)'
                    }
                  }}
                >
                  <option></option>
                  {input.options.map((option) => {
                    return (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            )
          )}
        </DialogContent>
        <DialogActions
        //classes={{ root: classes.buttonContainer }}
          sx={{
            "&": {
              padding: '16px 24px'
            }
          }}>
          <Button disabled={loading} onClick={() => handleClose()}>
            Cancel
          </Button>
          <Button
            disabled={loading}
            type={"submit"}
            color="primary"
            variant={"contained"}
          >
            Create Project
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

NewProjectDialog.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  inputs: PropTypes.array,
  confirmAction: PropTypes.func,
  open: PropTypes.bool,
};

NewProjectDialog.defaultProps = {
  title: "Dialog Title",
  description: "The dialog description should describe what this project does.",
  inputs: [
    {
      type: "text",
      label: "Text Input",
      id: "text-input",
    },
    {
      type: "select",
      label: "Select Input",
      id: "option-select",
      options: [
        {
          value: "123",
          label: "Option 1",
        },
      ],
    },
  ],
  confirmAction: () => {
    console.log("Created New Project");
  },
  open: false,
};

export default withRouter(NewProjectDialog);
